import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card, Container, Row, Col, Alert, Spinner, Badge, Toast, ToastContainer, Button, ButtonGroup } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { Helmet } from "react-helmet";
import { useAuth } from "../../contexts/AuthContext";
import { auth, fires } from "../../firebase";
import firebase from "firebase";
import GoogleMapReact from "google-map-react";
import { useNavigate } from "react-router-dom";
import "./AdminPageViewMap.css";

// Marker component for the map
const MapMarker = ({ lat, lng, pageView, onClick, isActive, isNew, userData }) => (
  <div 
    className="map-marker"
    onClick={onClick}
    style={{
      position: 'absolute',
      transform: 'translate(-50%, -100%)',
      cursor: 'pointer',
      zIndex: isActive ? 1000 : 1
    }}
  >
    <div 
      style={{
        width: '20px',
        height: '20px',
        backgroundColor: isActive ? '#dc3545' : (isNew ? '#28a745' : '#007bff'),
        borderRadius: '50%',
        border: '2px solid white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        transition: 'all 0.3s ease',
        animation: isNew ? 'pulse 2s infinite' : 'none'
      }}
    />
    {isActive && (
      <div 
        className="map-marker-info"
        style={{
          position: 'absolute',
          top: '25px',
          left: '50%',
          transform: 'translateX(-50%)',
          whiteSpace: 'nowrap',
          zIndex: 1001
        }}
      >
        <div><strong>User:</strong> {pageView.uid ? (userData[pageView.uid]?.displayName || userData[pageView.uid]?.email || 'Loading...') : 'Anonymous'}</div>
        <div><strong>URL:</strong> {pageView.currentPath || pageView.url || 'Unknown'}</div>
        <div><strong>IP:</strong> {pageView.currentIP}</div>
        <div><strong>Time:</strong> {pageView.timestamp?.toDate().toLocaleString()}</div>
        <div><strong>Location:</strong> {pageView.city}, {pageView.region}</div>
        {pageView.eventId && <div><strong>Event:</strong> {pageView.eventId}</div>}
      </div>
    )}
  </div>
);

export default function AdminPageViewMap() {
  const maxW = isMobile ? "100%" : "90%";
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [pageViews, setPageViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 43.6532, lng: -79.3832 }); // Default to Toronto
  const [mapZoom, setMapZoom] = useState(10);
  const [previousPageViews, setPreviousPageViews] = useState([]);
  const [autoZoomEnabled, setAutoZoomEnabled] = useState(true);
  const [newMarkerIds, setNewMarkerIds] = useState(new Set());
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [userData, setUserData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [touristAttractionPage, setTouristAttractionPage] = useState(1);
  const [campaignTouristPage, setCampaignTouristPage] = useState(1);
  const [stats, setStats] = useState({
    totalViews: 0,
    uniqueIPs: 0,
    countries: 0,
    cities: 0,
    campaigns: 0,
    uniqueCampaignIds: [],
    events: 0,
    uniqueEventIds: [],
    campaignAnalytics: {},
    campaignViews: 0,
    touristAttractions: 0,
    touristAttractionVisits: [],
    campaignTouristVisits: []
  });
  const [dateRange, setDateRange] = useState('7days'); // Default to past 7 days
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const unsubscribeRef = useRef(null);

  // Helper function to calculate date ranges
  const calculateDateRange = (range) => {
    const now = new Date();
    const end = new Date(now);
    let start = new Date(now);

    switch (range) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case '7days':
        start.setDate(now.getDate() - 7);
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      default:
        start.setDate(now.getDate() - 7);
    }

    return { start, end };
  };

  // Helper function to extract campaign ID from URL
  const extractCampaignId = (url) => {
    if (!url) return null;
    const match = url.match(/campaignId=([^&]+)/);
    return match ? match[1] : null;
  };

  // Helper function to extract event ID from URL
  const extractEventId = (url) => {
    if (!url) return null;
    const match = url.match(/\/event\/([^/?]+)/);
    return match ? match[1] : null;
  };

  // Helper function to extract email from URL
  const extractEmail = (url) => {
    if (!url) return null;
    const match = url.match(/email=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  // Helper function to check if URL is tourist attraction
  const isTouristAttraction = (url) => {
    if (!url) return false;
    return url.includes('category/tourist-attractions');
  };

  const loadPageViewsRealtime = useCallback(() => {
    // Clean up existing subscription if any
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    // Calculate date range based on current selection
    const { start, end } = calculateDateRange(dateRange);
    setStartDate(start);
    setEndDate(end);

    const newUnsubscribe = fires
      .collection('page_views')
      .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(start))
      .where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(end))
      .orderBy('timestamp', 'desc')
      .onSnapshot(
        (snapshot) => {
          const views = [];
          const uniqueIPs = new Set();
          const countries = new Set();
          const cities = new Set();
          const campaignIds = new Set();
          const eventIds = new Set();
          const campaignAnalytics = {};
          let campaignViews = 0;
          const touristAttractionVisits = [];
          const campaignTouristVisits = [];

          snapshot.docs.forEach((doc) => {
            const viewData = doc.data();
            const viewWithId = {
              id: doc.id,
              ...viewData,
              timestamp: viewData.timestamp
            };

            // Only include views with valid coordinates
            if (viewData.lat && viewData.lon && 
                typeof viewData.lat === 'number' && typeof viewData.lon === 'number' &&
                viewData.lat !== 0 && viewData.lon !== 0) {
              views.push(viewWithId);
              
              if (viewData.currentIP) {
                uniqueIPs.add(viewData.currentIP);
              }
              if (viewData.region) {
                countries.add(viewData.region);
              }
              if (viewData.city) {
                cities.add(viewData.city);
              }
              
              // Extract campaign and event information
              const url = viewData.currentPath || viewData.url;
              if (url) {
                const campaignId = extractCampaignId(url);
                const eventId = extractEventId(url);
                const email = extractEmail(url);
                
                // Track tourist attraction visits
                if (isTouristAttraction(url)) {
                  touristAttractionVisits.push({
                    id: viewWithId.id,
                    url: url,
                    timestamp: viewData.timestamp,
                    user: viewData.uid ? (userData[viewData.uid]?.displayName || userData[viewData.uid]?.email || 'Loading...') : 'Anonymous',
                    uid: viewData.uid,
                    email: email,
                    ip: viewData.currentIP,
                    location: viewData.city && viewData.region ? `${viewData.city}, ${viewData.region}` : 'Unknown',
                    campaignId: campaignId
                  });
                }
                
                // Track campaign tourist visits (campaigns with email parameter)
                if (campaignId && email) {
                  campaignTouristVisits.push({
                    id: viewWithId.id,
                    campaignId: campaignId,
                    email: email,
                    url: url,
                    timestamp: viewData.timestamp,
                    user: viewData.uid ? (userData[viewData.uid]?.displayName || userData[viewData.uid]?.email || 'Loading...') : 'Anonymous',
                    uid: viewData.uid,
                    ip: viewData.currentIP,
                    location: viewData.city && viewData.region ? `${viewData.city}, ${viewData.region}` : 'Unknown'
                  });
                }
                
                if (campaignId) {
                  campaignIds.add(campaignId);
                  campaignViews++;
                  
                  // Initialize campaign analytics if not exists
                  if (!campaignAnalytics[campaignId]) {
                    campaignAnalytics[campaignId] = {
                      views: 0,
                      events: new Set(),
                      eventVisits: {},
                      uniqueIPs: new Set(),
                      locations: new Set()
                    };
                  }
                  
                  // Update campaign analytics
                  campaignAnalytics[campaignId].views++;
                  if (eventId) {
                    campaignAnalytics[campaignId].events.add(eventId);
                    // Track event visit count
                    if (!campaignAnalytics[campaignId].eventVisits[eventId]) {
                      campaignAnalytics[campaignId].eventVisits[eventId] = 0;
                    }
                    campaignAnalytics[campaignId].eventVisits[eventId]++;
                  }
                  if (viewData.currentIP) {
                    campaignAnalytics[campaignId].uniqueIPs.add(viewData.currentIP);
                  }
                  if (viewData.city && viewData.region) {
                    campaignAnalytics[campaignId].locations.add(`${viewData.city}, ${viewData.region}`);
                  }
                }
                
                if (eventId) {
                  eventIds.add(eventId);
                }
              }
            }
          });

          // Update states first
          setPageViews(views);
          setPreviousPageViews(views);
          
          // Reset to first page when new data is loaded
          if (currentPage !== 1) {
            setCurrentPage(1);
          }
          
          // Load user data for any new UIDs found
          const uniqueUIDs = [...new Set(views.map(view => view.uid).filter(uid => uid && !userData[uid]))];
          uniqueUIDs.forEach(uid => {
            loadUserData(uid);
          });
          
          // Check for new page views and zoom to them if auto-zoom is enabled
          if (autoZoomEnabled && !isInitialLoad && previousPageViews.length > 0) {
            // Find new views by comparing IDs
            const newViews = views.filter(view => 
              !previousPageViews.some(prevView => prevView.id === view.id)
            );
            
            if (newViews.length > 0) {
              // Mark new views as new markers
              const newIds = new Set(newViews.map(view => view.id));
              setNewMarkerIds(newIds);
              
              // Show toast notification
              setToastMessage(`${newViews.length} new page view${newViews.length > 1 ? 's' : ''} detected!`);
              setShowToast(true);
              
              // Clear new marker status after 5 seconds
              setTimeout(() => {
                setNewMarkerIds(new Set());
              }, 5000);
              
              // Zoom to the most recent new view
              const latestNewView = newViews[0]; // Most recent is first due to desc order
              setMapCenter({ lat: latestNewView.lat, lng: latestNewView.lon });
              setMapZoom(15); // Zoom in closer for new views
              
              // Reset zoom after 3 seconds
              setTimeout(() => {
                setMapZoom(10);
              }, 3000);
            }
          }
          
          // Mark initial load as complete after first data load
          if (isInitialLoad) {
            setIsInitialLoad(false);
          }
          // Convert Sets to Arrays for campaign analytics
          const processedCampaignAnalytics = {};
          Object.keys(campaignAnalytics).forEach(campaignId => {
            const eventVisits = campaignAnalytics[campaignId].eventVisits;
            // Sort events by visit count (highest to lowest)
            const sortedEvents = Object.keys(eventVisits).sort((a, b) => eventVisits[b] - eventVisits[a]);
            
            processedCampaignAnalytics[campaignId] = {
              views: campaignAnalytics[campaignId].views,
              events: Array.from(campaignAnalytics[campaignId].events).sort(),
              eventVisits: eventVisits,
              sortedEvents: sortedEvents,
              uniqueIPs: campaignAnalytics[campaignId].uniqueIPs.size,
              locations: Array.from(campaignAnalytics[campaignId].locations).sort()
            };
          });

          setStats({
            totalViews: views.length,
            uniqueIPs: uniqueIPs.size,
            countries: countries.size,
            cities: cities.size,
            campaigns: campaignIds.size,
            uniqueCampaignIds: Array.from(campaignIds).sort(),
            events: eventIds.size,
            uniqueEventIds: Array.from(eventIds).sort(),
            campaignAnalytics: processedCampaignAnalytics,
            campaignViews: campaignViews,
            touristAttractions: touristAttractionVisits.length,
            touristAttractionVisits: touristAttractionVisits.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate()),
            campaignTouristVisits: campaignTouristVisits.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate())
          });

          setLoading(false);
        },
        (err) => {
          setError("Error loading page views: " + err.message);
          setLoading(false);
        }
      );

    // Store the unsubscribe function
    unsubscribeRef.current = newUnsubscribe;
  }, [dateRange]);

  useEffect(() => {
    // Initialize date range
    const { start, end } = calculateDateRange(dateRange);
    setStartDate(start);
    setEndDate(end);
  }, []);

  useEffect(() => {
    // Check admin access with management role
    if (!currentUser) {
      navigate('/');
      return;
    }
    
    fires
      .collection('admin')
      .doc(currentUser.uid)
      .get()
      .then((snapshot) => {
        if (snapshot.exists) {
          const adminInfo = snapshot.data();
          if (adminInfo.roles && adminInfo.roles.includes('management')) {
            loadPageViewsRealtime();
          } else {
            setError("Access denied. Management role required.");
            setLoading(false);
            navigate('/');
          }
        } else {
          setError("Access denied. Admin privileges required.");
          setLoading(false);
          navigate('/');
        }
      })
      .catch((err) => {
        setError("Error checking admin access: " + err.message);
        setLoading(false);
        navigate('/');
      });
  }, [currentUser, dateRange, loadPageViewsRealtime, navigate]); // Re-run when date range changes

  // Cleanup subscription on component unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const loadUserData = async (uid) => {
    if (!uid || userData[uid]) return userData[uid]; // Return cached data if available
    
    try {
      const userDoc = await fires.collection('users').doc(uid).get();
      if (userDoc.exists) {
        const userDataFromFirestore = userDoc.data();
        const userInfo = {
          id: uid,
          firstName: userDataFromFirestore.firstName || '',
          lastName: userDataFromFirestore.lastName || '',
          email: userDataFromFirestore.email || '',
          displayName: userDataFromFirestore.displayName || ''
        };
        
        // Update userData state with new user
        setUserData(prev => ({
          ...prev,
          [uid]: userInfo
        }));
        
        return userInfo;
      }
    } catch (error) {
      console.error('Error loading user data for UID:', uid, error);
    }
    return null;
  };

  const handleMarkerClick = (pageView) => {
    setSelectedMarker(selectedMarker?.id === pageView.id ? null : pageView);
  };

  const handleMapChange = ({ center, zoom }) => {
    setMapCenter(center);
    setMapZoom(zoom);
  };

  const resetToToronto = () => {
    setMapCenter({ lat: 43.6532, lng: -79.3832 });
    setMapZoom(10);
  };

  const toggleAutoZoom = () => {
    setAutoZoomEnabled(!autoZoomEnabled);
  };

  const getPaginatedPageViews = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return pageViews.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(pageViews.length / pageSize);
  const totalTouristAttractionPages = Math.ceil(stats.touristAttractionVisits.length / pageSize);
  const totalCampaignTouristPages = Math.ceil(stats.campaignTouristVisits.length / pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleTouristAttractionPageChange = (page) => {
    setTouristAttractionPage(page);
  };

  const handleCampaignTouristPageChange = (page) => {
    setCampaignTouristPage(page);
  };

  const getPaginatedTouristAttractionVisits = () => {
    const startIndex = (touristAttractionPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return stats.touristAttractionVisits.slice(startIndex, endIndex);
  };

  const getPaginatedCampaignTouristVisits = () => {
    const startIndex = (campaignTouristPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return stats.campaignTouristVisits.slice(startIndex, endIndex);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const { start, end } = calculateDateRange(range);
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1); // Reset to first page when changing date range
    setTouristAttractionPage(1); // Reset tourist attraction page
    setCampaignTouristPage(1); // Reset campaign tourist page
  };





  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <div className="text-center">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Loading page view map...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <ToastContainer position="top-end" className="p-3">
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg="success"
        >
          <Toast.Header>
            <strong className="me-auto">New Page View</strong>
            <small>Just now</small>
          </Toast.Header>
          <Toast.Body className="text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <Helmet>
        <title>Admin Page View Map</title>
        <meta property="og:title" content="Admin Page View Map" />
        <meta property="og:description" content="Real-time page view analytics with location data" />
        <meta property="og:url" content="https://evtgo.com/admin-page-view-map" />
        <meta property="og:site_name" content="Admin Page View Map" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Container
        className="d-flex justify-content-center"
        style={{
          minHeight: "100vh",
          minWidth: "100vw",
          backgroundColor: "white",
        }}
      >
        <div style={{ width: maxW, marginTop: "25px", marginBottom: "25px" }}>
          <Card className="border-0" style={{ backgroundColor: "transparent" }}>
            <Card.Body>
              <div style={{ marginBottom: "20px" }}>
                <Card.Title className="text-center" style={{ fontSize: '28px', fontWeight: 'bold' }}>
                  Page View Analytics Map
                </Card.Title>
                <Card.Text className="text-center" style={{ fontSize: '16px', color: '#666' }}>
                  Real-time page views with location data
                </Card.Text>
              </div>

              {/* Date Range Selector */}
              <Row className="mb-4">
                <Col className="d-flex justify-content-center">
                  <Card className="border-0" style={{ backgroundColor: 'transparent' }}>
                    <Card.Body className="p-3">
                      <div className="text-center mb-2">
                        <small className="text-muted">Date Range</small>
                      </div>
                      <ButtonGroup size="sm">
                        <Button
                          variant={dateRange === 'today' ? 'primary' : 'outline-primary'}
                          onClick={() => handleDateRangeChange('today')}
                        >
                          Today
                        </Button>
                        <Button
                          variant={dateRange === '7days' ? 'primary' : 'outline-primary'}
                          onClick={() => handleDateRangeChange('7days')}
                        >
                          Past 7 Days
                        </Button>
                        <Button
                          variant={dateRange === 'thisMonth' ? 'primary' : 'outline-secondary'}
                          onClick={() => handleDateRangeChange('thisMonth')}
                        >
                          This Month
                        </Button>
                      </ButtonGroup>
                      {startDate && endDate && (
                        <div className="text-center mt-2">
                          <small className="text-muted">
                            {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Statistics Cards */}
              <Row className="mb-4">
                <Col md={3} sm={6} className="mb-3">
                  <Card className="text-center h-100 stats-card">
                    <Card.Body>
                      <div className="fw-normal text-muted">Total Views</div>
                      <div className="fw-bold mt-2" style={{ fontSize: '24px', color: '#007bff' }}>
                        {stats.totalViews}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                  <Card className="text-center h-100 stats-card">
                    <Card.Body>
                      <div className="fw-normal text-muted">Unique IPs</div>
                      <div className="fw-bold mt-2" style={{ fontSize: '24px', color: '#28a745' }}>
                        {stats.uniqueIPs}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                  <Card className="text-center h-100 stats-card">
                    <Card.Body>
                      <div className="fw-normal text-muted">Countries</div>
                      <div className="fw-bold mt-2" style={{ fontSize: '24px', color: '#ffc107' }}>
                        {stats.countries}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                  <Card className="text-center h-100 stats-card">
                    <Card.Body>
                      <div className="fw-normal text-muted">Cities</div>
                      <div className="fw-bold mt-2" style={{ fontSize: '24px', color: '#dc3545' }}>
                        {stats.cities}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Campaign and Event Analytics */}
              {(stats.campaigns > 0 || stats.events > 0) && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="h-100">
                      <Card.Header>
                        <h6 className="mb-0">
                          <i className="fas fa-bullhorn me-2"></i>
                          Campaign Analytics ({stats.campaigns} campaigns, {stats.campaignViews} total views)
                        </h6>
                      </Card.Header>
                      <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {stats.campaigns > 0 ? (
                          <div>
                            {Object.keys(stats.campaignAnalytics).map((campaignId) => {
                              const analytics = stats.campaignAnalytics[campaignId];
                              return (
                                <div key={campaignId} className="mb-3 p-3 border rounded">
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">
                                      <Badge bg="primary" className="me-2">
                                        {campaignId}
                                      </Badge>
                                      Campaign
                                    </h6>
                                    <div className="text-end">
                                      <div className="fw-bold">{analytics.views} total views</div>
                                      <div className="text-muted small">{analytics.uniqueIPs} unique visitors</div>
                                    </div>
                                  </div>
                                  
                                  <div className="row">
                                    <div className="col-md-6">
                                      <div className="mb-3">
                                        <strong>Events Visited ({analytics.events.length}) - Ranked by Visits:</strong>
                                        <div className="mt-2">
                                          {analytics.sortedEvents && analytics.sortedEvents.length > 0 ? (
                                            analytics.sortedEvents.map((eventId, index) => (
                                              <div key={eventId} className="d-flex justify-content-between align-items-center mb-1 p-2 bg-light rounded">
                                                <div className="d-flex align-items-center">
                                                  <Badge bg="secondary" className="me-2">
                                                    #{index + 1}
                                                  </Badge>
                                                  <Badge bg="success">
                                                    Event {eventId}
                                                  </Badge>
                                                </div>
                                                <div className="text-end">
                                                  <div className="fw-bold">{analytics.eventVisits[eventId]} visits</div>
                                                  <small className="text-muted">Rank {index + 1}</small>
                                                </div>
                                              </div>
                                            ))
                                          ) : (
                                            <div className="text-muted small">No events visited</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="col-md-6">
                                      <div className="mb-3">
                                        <strong>Visitors ({analytics.uniqueIPs}):</strong>
                                        <div className="mt-2">
                                          {analytics.locations.length > 0 ? (
                                            analytics.locations.map((location, index) => (
                                              <div key={index} className="d-flex justify-content-between align-items-center mb-1 p-2 bg-light rounded">
                                                <span className="small">{location}</span>
                                                <small className="text-muted">Visitor</small>
                                              </div>
                                            ))
                                          ) : (
                                            <div className="text-muted small">No location data</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center text-muted py-4">
                            <i className="fas fa-bullhorn fa-2x mb-3"></i>
                            <div>No campaigns detected</div>
                            <small>Campaign page views will appear here</small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Tourist Attractions Analytics */}
              {(stats.touristAttractions > 0 || stats.campaignTouristVisits.length > 0) && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="h-100">
                      <Card.Header>
                        <h6 className="mb-0">
                          <i className="fas fa-map-marker-alt me-2"></i>
                          Tourist Attractions Analytics ({stats.touristAttractions} direct visits, {stats.campaignTouristVisits.length} campaign visits)
                        </h6>
                      </Card.Header>
                      <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {/* Direct Tourist Attraction Visits */}
                        {stats.touristAttractions > 0 && (
                          <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6 className="mb-0">
                                <Badge bg="success" className="me-2">
                                  Direct Visits
                                </Badge>
                                Tourist Attraction Page Views ({stats.touristAttractions})
                              </h6>
                              <div className="text-muted small">
                                Showing {((touristAttractionPage - 1) * pageSize) + 1} - {Math.min(touristAttractionPage * pageSize, stats.touristAttractionVisits.length)} of {stats.touristAttractionVisits.length}
                              </div>
                            </div>
                            <div className="table-responsive">
                              <table className="table table-sm">
                                <thead>
                                  <tr>
                                    <th>Time</th>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Location</th>
                                    <th>IP</th>
                                    <th>Campaign</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getPaginatedTouristAttractionVisits().map((visit) => (
                                    <tr key={visit.id}>
                                      <td className="small">
                                        {visit.timestamp?.toDate().toLocaleString()}
                                      </td>
                                      <td className="small">
                                        {visit.user}
                                      </td>
                                      <td className="small">
                                        {visit.email ? (
                                          <Badge bg="info">{visit.email}</Badge>
                                        ) : (
                                          <span className="text-muted">-</span>
                                        )}
                                      </td>
                                      <td className="small">
                                        {visit.location}
                                      </td>
                                      <td className="small">
                                        {visit.ip}
                                      </td>
                                      <td className="small">
                                        {visit.campaignId ? (
                                          <Badge bg="primary">{visit.campaignId}</Badge>
                                        ) : (
                                          <span className="text-muted">Direct</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            
                            {/* Pagination for Tourist Attraction Visits */}
                            {totalTouristAttractionPages > 1 && (
                              <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted small">
                                  Page {touristAttractionPage} of {totalTouristAttractionPages}
                                </div>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleTouristAttractionPageChange(1)}
                                    disabled={touristAttractionPage === 1}
                                  >
                                    <i className="fas fa-angle-double-left"></i>
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleTouristAttractionPageChange(touristAttractionPage - 1)}
                                    disabled={touristAttractionPage === 1}
                                  >
                                    <i className="fas fa-angle-left"></i>
                                  </Button>
                                  
                                  {/* Page Numbers */}
                                  {Array.from({ length: Math.min(5, totalTouristAttractionPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalTouristAttractionPages <= 5) {
                                      pageNum = i + 1;
                                    } else if (touristAttractionPage <= 3) {
                                      pageNum = i + 1;
                                    } else if (touristAttractionPage >= totalTouristAttractionPages - 2) {
                                      pageNum = totalTouristAttractionPages - 4 + i;
                                    } else {
                                      pageNum = touristAttractionPage - 2 + i;
                                    }
                                    
                                    return (
                                      <Button
                                        key={pageNum}
                                        variant={touristAttractionPage === pageNum ? "primary" : "outline-primary"}
                                        size="sm"
                                        onClick={() => handleTouristAttractionPageChange(pageNum)}
                                      >
                                        {pageNum}
                                      </Button>
                                    );
                                  })}
                                  
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleTouristAttractionPageChange(touristAttractionPage + 1)}
                                    disabled={touristAttractionPage === totalTouristAttractionPages}
                                  >
                                    <i className="fas fa-angle-right"></i>
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleTouristAttractionPageChange(totalTouristAttractionPages)}
                                    disabled={touristAttractionPage === totalTouristAttractionPages}
                                  >
                                    <i className="fas fa-angle-double-right"></i>
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Campaign Tourist Visits */}
                        {stats.campaignTouristVisits.length > 0 && (
                          <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6 className="mb-0">
                                <Badge bg="warning" className="me-2">
                                  Campaign Visits
                                </Badge>
                                Campaign Visits with Email Tracking ({stats.campaignTouristVisits.length})
                              </h6>
                              <div className="text-muted small">
                                Showing {((campaignTouristPage - 1) * pageSize) + 1} - {Math.min(campaignTouristPage * pageSize, stats.campaignTouristVisits.length)} of {stats.campaignTouristVisits.length}
                              </div>
                            </div>
                            <div className="table-responsive">
                              <table className="table table-sm">
                                <thead>
                                  <tr>
                                    <th>Time</th>
                                    <th>Campaign ID</th>
                                    <th>Email</th>
                                    <th>User</th>
                                    <th>Location</th>
                                    <th>IP</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getPaginatedCampaignTouristVisits().map((visit) => (
                                    <tr key={visit.id}>
                                      <td className="small">
                                        {visit.timestamp?.toDate().toLocaleString()}
                                      </td>
                                      <td className="small">
                                        <Badge bg="primary">{visit.campaignId}</Badge>
                                      </td>
                                      <td className="small">
                                        <Badge bg="info">{visit.email}</Badge>
                                      </td>
                                      <td className="small">
                                        {visit.user}
                                      </td>
                                      <td className="small">
                                        {visit.location}
                                      </td>
                                      <td className="small">
                                        {visit.ip}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            
                            {/* Pagination for Campaign Tourist Visits */}
                            {totalCampaignTouristPages > 1 && (
                              <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted small">
                                  Page {campaignTouristPage} of {totalCampaignTouristPages}
                                </div>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleCampaignTouristPageChange(1)}
                                    disabled={campaignTouristPage === 1}
                                  >
                                    <i className="fas fa-angle-double-left"></i>
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleCampaignTouristPageChange(campaignTouristPage - 1)}
                                    disabled={campaignTouristPage === 1}
                                  >
                                    <i className="fas fa-angle-left"></i>
                                  </Button>
                                  
                                  {/* Page Numbers */}
                                  {Array.from({ length: Math.min(5, totalCampaignTouristPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalCampaignTouristPages <= 5) {
                                      pageNum = i + 1;
                                    } else if (campaignTouristPage <= 3) {
                                      pageNum = i + 1;
                                    } else if (campaignTouristPage >= totalCampaignTouristPages - 2) {
                                      pageNum = totalCampaignTouristPages - 4 + i;
                                    } else {
                                      pageNum = campaignTouristPage - 2 + i;
                                    }
                                    
                                    return (
                                      <Button
                                        key={pageNum}
                                        variant={campaignTouristPage === pageNum ? "primary" : "outline-primary"}
                                        size="sm"
                                        onClick={() => handleCampaignTouristPageChange(pageNum)}
                                      >
                                        {pageNum}
                                      </Button>
                                    );
                                  })}
                                  
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleCampaignTouristPageChange(campaignTouristPage + 1)}
                                    disabled={campaignTouristPage === totalCampaignTouristPages}
                                  >
                                    <i className="fas fa-angle-right"></i>
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleCampaignTouristPageChange(totalCampaignTouristPages)}
                                    disabled={campaignTouristPage === totalCampaignTouristPages}
                                  >
                                    <i className="fas fa-angle-double-right"></i>
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {stats.touristAttractions === 0 && stats.campaignTouristVisits.length === 0 && (
                          <div className="text-center text-muted py-4">
                            <i className="fas fa-map-marker-alt fa-2x mb-3"></i>
                            <div>No tourist attraction visits detected</div>
                            <small>Visits to tourist attraction pages and campaign visits with email tracking will appear here</small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Map Controls */}
              <Row className="mb-3">
                <Col>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={resetToToronto}
                    >
                      <i className="fas fa-home me-1"></i>
                      Reset to Toronto
                    </Button>
                    <Button 
                      variant={autoZoomEnabled ? "success" : "outline-secondary"}
                      size="sm"
                      onClick={toggleAutoZoom}
                    >
                      <i className="fas fa-crosshairs me-1"></i>
                      {autoZoomEnabled ? "Auto-zoom ON" : "Auto-zoom OFF"}
                    </Button>
                    {autoZoomEnabled && (
                      <Badge bg="info" className="d-flex align-items-center">
                        <i className="fas fa-info-circle me-1"></i>
                        Will zoom to new page views
                      </Badge>
                    )}

                  </div>
                </Col>
              </Row>

              {/* Map Container */}
              <Card className="mb-4">
                <Card.Body style={{ padding: 0 }}>
                  <div className="map-container" style={{ height: isMobile ? '400px' : '600px', width: '100%' }}>
                    <GoogleMapReact
                      bootstrapURLKeys={{ 
                        key: process.env.REACT_APP_GOOGLE_API || '' 
                      }}
                      center={mapCenter}
                      zoom={mapZoom}
                      onChange={handleMapChange}
                      options={{
                        styles: [
                          {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }]
                          }
                        ]
                      }}
                    >
                      {pageViews.map((pageView) => (
                        <MapMarker
                          key={pageView.id}
                          lat={pageView.lat}
                          lng={pageView.lon}
                          pageView={pageView}
                          onClick={() => handleMarkerClick(pageView)}
                          isActive={selectedMarker?.id === pageView.id}
                          isNew={newMarkerIds.has(pageView.id)}
                          userData={userData}
                        />
                      ))}
                    </GoogleMapReact>
                  </div>
                </Card.Body>
              </Card>

              {/* Recent Activity */}
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Recent Page Views</h5>
                  <div className="text-muted small">
                    Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pageViews.length)} of {pageViews.length}
                  </div>
                </Card.Header>
                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                     {getPaginatedPageViews().map((pageView) => {
                       const user = pageView.uid ? userData[pageView.uid] : null;
                       const userName = user ? 
                         (user.displayName || `${user.firstName} ${user.lastName}`.trim() || user.email) : 
                         (pageView.uid ? 'Loading...' : 'Anonymous');
                       
                       // URL categorization logic
                       const getUrlCategory = (url) => {
                         if (!url) return 'Unknown';
                         
                         // Check if it's from old website
                         if (url.includes('evtgo.com')) {
                           return <span className="fw-bold text-danger">Old Website</span>;
                         }
                         
                         // Check if it's from eventgo.ca
                         if (url.includes('eventgo.ca')) {
                           const path = url.split('eventgo.ca')[1] || '';
                           
                           // Check for campaign
                           if (path.includes('/event') && path.includes('campaignId=')) {
                             return <span className="fw-bold text-primary">Campaign</span>;
                           }
                           
                           // Check for specific paths
                           if (path === '/' || path === '') {
                             return <span className="fw-bold">Homepage</span>;
                           }
                           if (path.startsWith('/host')) {
                             return <span className="fw-bold">Host Page</span>;
                           }
                           if (path.startsWith('/event')) {
                             return <span className="fw-bold">Event Page</span>;
                           }
                           
                           return <span className="text-muted">{path || 'Homepage'}</span>;
                         }
                         
                         return <span className="text-muted">{url}</span>;
                       };
                       
                       const urlCategory = getUrlCategory(pageView.currentPath || pageView.url);
                       
                       return (
                         <div 
                           key={pageView.id} 
                           className={`d-flex justify-content-between align-items-start mb-2 p-2 border-bottom recent-activity-item ${
                             selectedMarker?.id === pageView.id ? 'selected' : ''
                           }`}
                           style={{ cursor: 'pointer' }}
                           onClick={() => handleMarkerClick(pageView)}
                         >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="fw-bold text-truncate">
                              {urlCategory}
                            </div>
                            <div className="text-muted small">
                              <strong>User:</strong> {userName}
                            </div>
                            <div className="text-muted small">
                              <strong>URL:</strong> {pageView.currentPath || pageView.url || 'Unknown'}
                            </div>
                            <div className="text-muted small">
                              {pageView.city && pageView.region 
                                ? `${pageView.city}, ${pageView.region}`
                                : 'Location unknown'
                              }
                            </div>
                            <div className="text-muted small">
                              {pageView.timestamp?.toDate().toLocaleString()}
                            </div>
                            <div className="text-muted small">
                              <strong>IP:</strong> {pageView.currentIP || 'Unknown IP'}
                            </div>
                          </div>
                          <div className="text-end ms-2" style={{ minWidth: 'fit-content' }}>
                            {pageView.eventId && (
                              <Badge bg="info" className="mb-1 d-block">
                                Event: {pageView.eventId}
                              </Badge>
                            )}
                            <div className="text-muted small">
                              {pageView.platform || 'web'}
                            </div>
                            {pageView.device && (
                              <div className="text-muted small">
                                {pageView.device}
                              </div>
                            )}
                          </div>
                        </div>
                       );
                     })}
                  {pageViews.length === 0 && (
                    <div className="text-center text-muted">
                      No page views with location data found
                    </div>
                  )}
                  
                  {/* Pagination Controls */}
                  {pageViews.length > 0 && totalPages > 1 && (
                    <Card.Footer className="d-flex justify-content-between align-items-center">
                      <div className="text-muted small">
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                        >
                          <i className="fas fa-angle-double-left"></i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="fas fa-angle-left"></i>
                        </Button>
                        
                        {/* Page Numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "primary" : "outline-primary"}
                              size="sm"
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                        
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <i className="fas fa-angle-right"></i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          <i className="fas fa-angle-double-right"></i>
                        </Button>
                      </div>
                    </Card.Footer>
                  )}
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  );
}
